/**
 * Ambient low-res image flag (DKPLAT-669).
 *
 * When a pickem opts in via `style_config.lowResImages`, team/jersey artwork is
 * served from the `-low` blob folders (e.g. `nfl/teams` -> `nfl/teams-low`,
 * `nfl/jersey` -> `nfl/jersey-low`). The suffixed folders hold the same-named
 * images at a lower resolution.
 *
 * The flag is set for the *synchronous* duration of a question adapter run (see
 * `QuestionRenderer`) and read by the two image-URL chokepoints
 * (`normalizeTeamLogoUrl` for API-provided URLs and `generateTeamLogoUrl` for
 * client-built ones). Adapters are pure synchronous transforms, so there is no
 * interleaving and the flag never leaks past a single adapter call.
 *
 * Frontend-only: this affects only how the game renders image URLs.
 */
let lowResEnabled = false;

export function setLowResImages(enabled: boolean): void {
  lowResEnabled = enabled;
}

export function isLowResImages(): boolean {
  return lowResEnabled;
}

// Matches an Azure blob image URL and splits off the parent folder of the file
// (the second-to-last path segment) so it can be suffixed. Leaves any query or
// hash attached to the filename. Non-blob URLs never match and pass through.
const BLOB_IMAGE_RE =
  /^(https?:\/\/[^/]+\.blob\.core\.windows\.net\/(?:.*\/)?)([^/?#]+)\/([^/?#]+(?:[?#].*)?)$/i;

/**
 * Rewrites a blob image URL to its low-res variant by suffixing the parent
 * folder of the file with `-low`. Idempotent, and a no-op for non-blob URLs.
 *   .../picks-core/nfl/teams/KC.png    -> .../picks-core/nfl/teams-low/KC.png
 *   .../picks-core/nfl/jersey/12.png   -> .../picks-core/nfl/jersey-low/12.png
 */
export function toLowResImageUrl(url: string): string {
  // Country flags (DKPLAT-606) are already 128px and have no `-low` copies.
  if (/\/flags\/(?:round|square)\//i.test(url)) return url;
  const match = url.match(BLOB_IMAGE_RE);
  if (!match) {
    return url;
  }

  const [, prefix, folder, file] = match;
  if (folder.endsWith('-low')) {
    return url;
  }

  return `${prefix}${folder}-low/${file}`;
}
